import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, Plus } from 'lucide-react';
import { updateProjectInstructions } from '../../../AGTHall/services/AGTHallService';
import { useProjectContext } from '../ProjectContextProvider';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

const StyledButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '20px',
    textTransform: 'none',
    padding: '6px 16px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.8)' : '#0371db',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: 6,
    },
  };
});

// Definir límites de caracteres
const MAX_TITLE_CHARS = 50;
const MAX_INSTRUCTIONS_CHARS = 1000;

const InstructionsView = ({ project, onBack, user }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation(); // Inicializar hook de traducción
  const { projectInstructions, updateProjectInstructions: updateInstructionsInProvider } = useProjectContext();

  // Obtener instrucciones del provider
  const currentInstructions = projectInstructions[project?.id] || {};
  const realInstructionsTitle = currentInstructions.instructionsTitle || '';
  const realInstructionsContent = currentInstructions.instructionsContent || '';

  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  // Cargar instrucciones iniciales
  useEffect(() => {
    if (project?.id && project?.contextData) {
      updateInstructionsInProvider(project.id, {
        instructionsTitle: project.contextData.instructionsTitle || '',
        instructionsContent: project.contextData.instructionsContent || ''
      });
    }
  }, [project?.id, project?.contextData, updateInstructionsInProvider]);

  const handleAddInstructions = () => {
    setOpenModal(true);
    setTitle('');
    setInstructions('');
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setTitle('');
    setInstructions('');
  };

  const handleSave = async () => {
    if (!project?.id || !user?.uid) return;

    setIsLoading(true);
    const finalTitle = title.trim() || t('projectViews.instructions.newInstruction');

    try {
      await updateProjectInstructions({
        userId: user.uid,
        projectId: project.id,
        instructionsTitle: finalTitle,
        instructionsContent: instructions
      });

      // Actualizar las instrucciones en el provider
      updateInstructionsInProvider(project.id, {
        instructionsTitle: finalTitle,
        instructionsContent: instructions
      });

      setSnackbarMessage(t('projectViews.instructions.instructionsSaved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseModal();

    } catch (error) {
      console.error('Error saving instructions:', error);
      setSnackbarMessage(t('projectViews.instructions.instructionsSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleOpenViewModal = () => {
    if (realInstructionsTitle || realInstructionsContent) {
      setOpenViewModal(true);
    }
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '800px',
            lg: '900px'
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                mr: 1,
                color: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.8)'
                  : (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700')
              }}
            >
              <ArrowLeft />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 500,
                color: isWallpaperMode ? '#FFFFFF' : 'inherit'
              }}
            >
              {t('projectViews.instructions.title')}
            </Typography>
          </Box>

          <StyledButton
            startIcon={<Plus size={18} />}
            onClick={handleAddInstructions}
            disabled={isLoading}
          >
            {t('projectViews.instructions.newInstruction')}
          </StyledButton>
        </Box>

        <List sx={{ px: 0 }}>
          {!realInstructionsTitle && !realInstructionsContent ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              {t('projectViews.instructions.noInstructions')}
            </Typography>
          ) : (
            <ListItem
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: '12px',
                cursor: 'pointer',
                backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                backgroundColor:
                  isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.12)'
                    : (theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(0, 0, 0, 0.02)'),
                '&:hover': {
                  backgroundColor:
                    isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.18)'
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.05)'),
                },
                transition: 'background-color 0.2s ease',
              }}
              onClick={handleOpenViewModal}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      mb: 0.5,
                      color: isWallpaperMode ? '#FFFFFF' : 'inherit'
                    }}
                  >
                    {realInstructionsTitle || t('projectViews.instructions.untitledInstructions')}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color:
                        isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.7)'
                          : (theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.5)'
                            : 'rgba(0, 0, 0, 0.5)')
                    }}
                  >
                    {realInstructionsContent.length > 60
                      ? `${realInstructionsContent.slice(0, 60)}...`
                      : realInstructionsContent}
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Box>

      {/* Modal para nuevas instrucciones */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {t('projectViews.instructions.newInstruction')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          {/* Campo de Título con límite de 50 caracteres */}
          <TextField
            autoFocus
            fullWidth
            placeholder={t('projectChatView.contextTitle')}
            value={title}
            onChange={(e) => {
              const newTitle = e.target.value.slice(0, MAX_TITLE_CHARS);
              setTitle(newTitle);
            }}
            variant="outlined"
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': { border: 'none' },
              }
            }}
          />
          {/* Contador de caracteres para el título (solo muestra el número) */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 2,
              textAlign: 'right',
              color: title.length >= MAX_TITLE_CHARS ? 'red' : 'inherit',
            }}
          >
            {MAX_TITLE_CHARS - title.length}
          </Typography>

          {/* Campo de Instrucciones con límite de 1000 caracteres */}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('projectChatView.instructionsPlaceholder')}
            value={instructions}
            onChange={(e) => {
              const newInstructions = e.target.value.slice(0, MAX_INSTRUCTIONS_CHARS);
              setInstructions(newInstructions);
            }}
            variant="outlined"
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': { border: 'none' },
              }
            }}
          />
          {/* Contador de caracteres para las instrucciones (solo muestra el número) */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 2,
              textAlign: 'right',
              color: instructions.length >= MAX_INSTRUCTIONS_CHARS ? 'red' : 'inherit',
            }}
          >
            {MAX_INSTRUCTIONS_CHARS - instructions.length}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 2,
              color: isWallpaperMode
                ? 'rgba(255, 255, 255, 0.7)'
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.5)'),
              fontSize: '0.7rem',
              textAlign: 'center'
            }}
          >
            {t('projectChatView.instructionsNote')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)')
              }
            }}
          >
            {t('projectViews.instructions.cancel')}
          </Button>
          <StyledButton
            onClick={handleSave}
            disabled={isLoading || !instructions.trim()}
          >
            {t('projectViews.instructions.save')}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Modal para ver instrucciones */}
      <Dialog
        open={openViewModal}
        onClose={handleCloseViewModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {realInstructionsTitle || t('projectViews.instructions.untitledInstructions')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {realInstructionsContent}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseViewModal}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)')
              }
            }}
          >
            {t('projectViews.instructions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InstructionsView;
